// Slice 9: Settings.
import { useEffect, useState } from "react";
import { Lock, LogOut, Mail, User as UserIcon } from "lucide-react";
import { Link } from "wouter";
import { useCohort, fp } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface Me { id: number; name: string; email: string; companyName: string | null; mustChangePassword: boolean; }

export default function FounderSettings() {
  const { slug } = useCohort();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}api/founders/me`, { headers: auth() });
        if (r.ok) {
          const d = await r.json();
          setMe({
            id: d.id, name: d.name, email: d.email,
            companyName: d.company?.name || null,
            mustChangePassword: !!d.mustChangePassword,
          });
        }
      } finally { setLoading(false); }
    })();
  }, []);

  function logout() {
    localStorage.removeItem("founder_token");
    window.location.replace(fp(slug, "login"));
  }

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  if (!me) return <div className="p-12 text-red-400 text-sm">Could not load your profile.</div>;

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Settings</div>
        <h1 className="text-2xl font-bold tracking-tight">Your account</h1>
      </div>

      <div className="space-y-4">
        <Row icon={<UserIcon className="w-3.5 h-3.5" />} label="Name" value={me.name} />
        <Row icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={me.email} />
        <Row icon={<UserIcon className="w-3.5 h-3.5" />} label="Company" value={me.companyName || "—"} />

        <Link href={fp(slug, "change-password")} className="block bg-white/[0.04] hover:bg-white/[0.06] border border-white/[0.08] rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Lock className="w-3.5 h-3.5 text-[#FF5C00]" /> Change password</div>
          <div className="text-xs text-white/60">{me.mustChangePassword ? "You still need to set your own password." : "Change anytime."}</div>
        </Link>

        <button
          onClick={logout}
          data-testid="button-logout"
          className="w-full bg-red-500/10 hover:bg-red-500/15 border border-red-500/25 text-red-300 text-sm font-semibold rounded-lg p-4 flex items-center justify-center gap-2"
        ><LogOut className="w-3.5 h-3.5" /> Sign out</button>
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.08] text-xs text-white/60 space-y-1">
        <div>Need help with your account or billing.</div>
        <div>Email <a href="mailto:pc@favcy.in" className="text-[#FF5C00] hover:text-[#FF5C00]/80">pc@favcy.in</a>.</div>
      </div>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-white/60 mb-1">{icon} {label}</div>
      <div className="text-sm text-white/90">{value}</div>
    </div>
  );
}
